import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { FunctionComponent } from 'react';

import {
  EventQuery,
  EventType,
  Milestone,
} from '../../../../apollo/__generated__/graphql';
import { useEditMilestoneDrawer } from './useEditMilestoneDrawer';

interface EditMilestoneDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  milestone: Milestone;
  event: EventQuery['event'];
}

export const EditMilestoneDrawer: FunctionComponent<
  EditMilestoneDrawerProps
> = ({ isOpen, onClose, event, milestone }) => {
  const {
    endAtValue,
    errors,
    onEndAtChange,
    onStartAtChange,
    onSubmit,
    register,
    startAtValue,
    priceOptions,
  } = useEditMilestoneDrawer(event, milestone, onClose);

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose}>
      <Box sx={{ mt: 10, mx: 2 }}>
        <Typography variant="h4">Créer un palier</Typography>
        <form onSubmit={onSubmit}>
          <TextField
            margin="normal"
            fullWidth
            id="name"
            label="Nom du palier"
            placeholder='Ex: "Palier 1"'
            autoFocus
            type="text"
            {...register('name')}
            helperText={
              errors.name?.message ? (
                <Typography component="p" variant="body2" color="error.main">
                  {errors.name.message}
                </Typography>
              ) : null
            }
          />
          {priceOptions.map((priceOption) => (
            <TextField
              key={priceOption}
              margin="normal"
              fullWidth
              label={`Prix pour ${priceOption + 1} soir en centimes`}
              placeholder='Ex: "5000" pour 50.00€'
              autoFocus
              type="number"
              {...register(`prices.${priceOption}`)}
              helperText={
                errors.prices?.[priceOption]?.message ? (
                  <Typography component="p" variant="body2" color="error.main">
                    {errors.prices[priceOption].message}
                  </Typography>
                ) : null
              }
            />
          ))}

          <TextField
            margin="normal"
            fullWidth
            id="noAlcoholDiscount"
            label="Réduction BT en centimes"
            placeholder='Ex: "800" pour 8.00€'
            autoFocus
            type="number"
            {...register('noAlcoholDiscount')}
            helperText={
              errors.noAlcoholDiscount?.message ? (
                <Typography component="p" variant="body2" color="error.main">
                  {errors.noAlcoholDiscount.message}
                </Typography>
              ) : null
            }
          />

          {event.type === EventType.BedroomAndTent ? (
            <TextField
              margin="normal"
              fullWidth
              id="bedroomValue"
              label="Supplément chambre en centimes"
              placeholder='Ex: "1000" pour 10.00€'
              autoFocus
              type="number"
              {...register('bedroomValue')}
              helperText={
                errors.bedroomValue?.message ? (
                  <Typography component="p" variant="body2" color="error.main">
                    {errors.bedroomValue.message}
                  </Typography>
                ) : null
              }
            />
          ) : null}

          <FormControlLabel
            control={<Checkbox {...register('isVoucherOnly')} />}
            label="Réservé aux personnes ayant un code de réduction"
          />

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="body2">Début</Typography>
              <DateTimePicker
                ampm={false}
                value={startAtValue}
                onChange={onStartAtChange}
              />
              {errors.startAt?.message ? (
                <Typography component="p" variant="body2" color="error.main">
                  {errors.startAt.message}
                </Typography>
              ) : null}
            </Box>
            <Typography variant="h4" sx={{ mx: 4 }}>
              -
            </Typography>
            <Box>
              <Typography variant="body2">Fin</Typography>
              <DateTimePicker
                ampm={false}
                value={endAtValue}
                onChange={onEndAtChange}
              />
              {errors.endAt?.message ? (
                <Typography component="p" variant="body2" color="error.main">
                  {errors.endAt.message}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            Modifier
          </Button>
        </form>
      </Box>
    </Drawer>
  );
};
